'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Slider } from '@/components/ui/slider';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

import { useHLSPlayer } from '../contexts/hls-player-provider';
import type { IControlsProps } from '../types';
import { formatTime } from '../utils';

function SeekBar({ className }: IControlsProps) {
	const { playbackState, seek } = useHLSPlayer();
	const { currentTime, duration } = playbackState;
	const [hoverTime, setHoverTime] = useState<number | null>(null);
	const [hoverPosition, setHoverPosition] = useState(0);
	const sliderRef = useRef<HTMLDivElement>(null);

	const playedPercentage = useMemo(() => {
		if (!duration) return 0;
		return (currentTime / duration) * 100;
	}, [currentTime, duration]);

	const handleValueChange = useCallback(
		(value: number[]) => {
			if (!duration) return;
			const time = (value[0] / 100) * duration;
			seek(time);
		},
		[duration, seek]
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLSpanElement>) => {
			const slider = sliderRef.current;
			if (!slider || !duration) return;

			const rect = slider.getBoundingClientRect();
			const percentage = (e.clientX - rect.left) / rect.width;
			const time = percentage * duration;

			setHoverTime(Math.max(0, Math.min(time, duration)));
			setHoverPosition(percentage * 100);
		},
		[duration]
	);

	const handleMouseLeave = useCallback(() => {
		setHoverTime(null);
	}, []);

	return (
		<div className={cn('group relative w-full px-2', className)}>
			<Slider
				ref={sliderRef}
				value={[playedPercentage]}
				onValueChange={handleValueChange}
				max={100}
				step={0.1}
				// Invisible vertical padding widens the hit area without thickening the visible track
				className="relative -my-2 cursor-pointer py-2"
				trackClassName="h-1 !bg-white/40 cursor-pointer"
				rangeClassName="!bg-white cursor-pointer"
				thumbClassName="h-3 w-3 border-0 !bg-white opacity-0 transition-all group-hover:scale-125 group-hover:opacity-100"
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
			/>

			{/* Hover tooltip */}
			{hoverTime !== null && (
				<Tooltip
					open={true}
					text={formatTime(hoverTime)}
				>
					<div
						className="pointer-events-none absolute top-1/2 h-full w-0.5 -translate-x-1/2 -translate-y-1/2"
						style={{ left: `${hoverPosition}%` }}
					/>
				</Tooltip>
			)}
		</div>
	);
}

export default React.memo(SeekBar);
